import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { BusinessSummaryResponseDTO } from 'src/app/shared/models/dtos/responses/business-summary-response-dto/business-summary-response-dto';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { BusinessStatus } from 'src/app/shared/models/constants/business-status';
import { BusinessLevel } from 'src/app/shared/models/business-level/business-level';
import { FamilyDTO } from 'src/app/shared/models/dtos/responses/family-dto/family-dto';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { CouponVip } from 'src/app/shared/models/dtos/responses/coupon-vip/coupon-vip';

@Component({
  selector: 'app-business-vip-details',
  templateUrl: './business-vip-details.component.html',
  styleUrls: ['./business-vip-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

/**
 * Shows basic infos about a business
 */
export class BusinessVipDetailsComponent implements OnInit {
  @Input() businessSummary?: BusinessSummaryResponseDTO;
  @Input() nRepresentative!: number;
  @Input() refreshAction!: () => void;
  @Input() businessVIPCoupons!: CouponVip[];
  @Input() viewOnly!: boolean;

  today = new Date().toISOString();

  readonly labels = {
    disabledBusinessMessage: 'disabled-business-message-global',
    reissueVipCouponSuccess: 'reissue-vip-coupon-success',
    reissueVipCouponError: 'reissue-vip-coupon-error',
  };

  /**
   * @constructor
   */
  constructor(
    private dialogService: DialogService,
    private translationService: TranslationService,
    private userService: UserService,
    private snackBarService: SnackBarService
  ) {}

  /**
   * on init
   */
  ngOnInit(): void {
    this.translationService.translateObject(this.labels);
    this.today = this.translationService.localizeDate(this.today);
  }

  /**
   * Checks is business is disabled
   * @return {boolean}
   */
  isDisabled(): boolean {
    const isBusinessDisabled =
      this.businessSummary?.status === BusinessStatus.DISABLED;
    const hasNoRepresentatives = this.nRepresentative === 0;

    return isBusinessDisabled && hasNoRepresentatives;
  }

  /**
   * @param {number} familyId
   * @return {boolean} indicates if the timeline is disabled
   */
  isTimelineDisabled(familyId: number): boolean {
    for (const activeLevel of this.businessSummary!.activeBusinessLevels!) {
      if (activeLevel.familyPk == familyId) {
        return false;
      }
    }
    return true;
  }

  /**
   * Retrieves the level associated for the next season to a determined family.
   * Returns null if no vip level is associated for next season
   * @param {number} familyId
   * @return {BusinessLevel}
   */
  getNextLevel(familyId: number): BusinessLevel {
    for (const nextBusinessLevel of this.businessSummary!.nextBusinessLevels!) {
      if (nextBusinessLevel.familyPk === familyId) {
        return nextBusinessLevel;
      }
    }
    return null!;
  }

  /**
   * Shows business editting modal
   * @param {number} familyId
   * @param {string} familyName
   */
  async openUpdateVipLevelModal(familyId: number, familyName: string) {
    this.dialogService.openUpdateVipLevelModal(
      this.businessSummary!,
      new FamilyDTO(familyId, familyName, [], 0),
      this.reloadBusinessVipData
    );
  }

  /**
   * Reloads business vip after modal is closed with a successful update
   */
  private reloadBusinessVipData = () => {
    this.refreshAction();
  };

  /**
   * Retrieves the VIP Coupon associated for the current season to a determined family.
   * Returns null if no VIP Coupon is associated for current season
   * @param {number} familyId
   * @return {CouponVip}
   */
  getVIPCoupon(familyId: number): CouponVip {
    for (const vipCoupon of this.businessVIPCoupons!) {
      if (vipCoupon.businessLevel.familyPk === familyId) {
        return vipCoupon;
      }
    }
    return null!;
  }

  /**
   * Determines if can reissue an VIP Coupon
   * @param {BusinessLevel} businessLevel
   * @return {boolean}
   */
  canReissueVIPCoupon(businessLevel: BusinessLevel): boolean {
    if (businessLevel.level === 0) {
      return false;
    }
    const vipCoupon: CouponVip = this.getVIPCoupon(businessLevel.familyPk);
    if (!vipCoupon) {
      return true;
    }
    return (
      !!vipCoupon.expirationDate &&
      new Date() > new Date(vipCoupon.expirationDate)
    );
  }

  /**
   * Shows a confirmation dialog to reissue VIP coupon
   * @param {BusinessLevel} businessLevel
   */
  async openReissueVIPCouponConfirmation(businessLevel: BusinessLevel) {
    this.dialogService.openDialog(
      {
        title: await this.translationService.translateString(
          'reissue-vip-coupon-modal.title'
        ),
        content: await this.translationService.translateString(
          'reissue-vip-coupon-modal.content',
          {
            businessName: this.businessSummary?.name,
            vipLevelName: businessLevel.description,
            todayDate: this.today,
          }
        ),
        successButtonText: await this.translationService.translateString(
          'reissue-vip-coupon-modal.reissue'
        ),
        failButtonText: await this.translationService.translateString(
          'reissue-vip-coupon-modal.cancel'
        ),
      },
      () => this.onReissueVIPCouponConfirm(businessLevel)
    );
  }

  /**
   * Action to reissue VIP coupon
   * @param {BusinessLevel} businessLevel
   */
  onReissueVIPCouponConfirm(businessLevel: BusinessLevel) {
    const season = 1; // Using season 1 to activate VIP coupon today

    const vipCoupon = this.getVIPCoupon(businessLevel.familyPk);

    const request = {
      businessLevel: businessLevel.level!,
      resellerName: vipCoupon !== null ? vipCoupon.resellerName! : null,
      familyId: businessLevel.familyPk,
      season,
    };

    this.userService
      .updateBusinessLevel(this.businessSummary!.id, request)
      .subscribe({
        next: () => {
          this.refreshAction();
        },
        error: () => {
          this.snackBarService.error(this.labels.reissueVipCouponError);
        },
        complete: () => {
          this.snackBarService.success(this.labels.reissueVipCouponSuccess);
        },
      });
  }
}
